import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateTaskIssueImport, commitTaskIssueImport, getSampleXlsx } from '../services/punchPointService';
import { downloadBlob } from '../utils/downloadFile';
import useUIStore from '../store/uiStore';

const TaskIssueImportPage = () => {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [downloadingSample, setDownloadingSample] = useState(false);
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setReport(null);
  };

  const handleDownloadSample = async () => {
    setDownloadingSample(true);
    try {
      const res = await getSampleXlsx();
      downloadBlob(res.data, 'task_issue_register_sample.xlsx');
    } catch (err) {
      showToast('Failed to generate sample file', 'error');
    }
    setDownloadingSample(false);
  };

  const handleValidate = async () => {
    if (!file) {
      showToast('Choose a file first', 'error');
      return;
    }
    setValidating(true);
    try {
      const res = await validateTaskIssueImport(file);
      setReport(res.data);
      if (res.data.valid) {
        showToast(`${res.data.valid_row_count} row(s) ready to import`, 'success');
      } else {
        showToast(`${res.data.error_count} row(s) need fixing before this can be imported`, 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to validate file', 'error');
      setReport(null);
    }
    setValidating(false);
  };

  const handleCommit = async () => {
    if (!file || !report?.valid) return;
    if (!window.confirm(`Import ${report.valid_row_count} entries into the Task and Issues Register?`)) return;
    setCommitting(true);
    try {
      const res = await commitTaskIssueImport(file);
      showToast(`Imported ${res.data.created_count} entries`, 'success');
      navigate('/punch-points');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Import failed - re-validate and try again', 'error');
      setReport(null);
    }
    setCommitting(false);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">Import Task / Issue Register</h1>
        <p className="text-sm text-gray-500 mb-6">
          Upload an .xlsx or .csv file with these columns: Entry by, Issue Type (Task/Issue), Project code,
          Project Name, Issue date, Issues, Issue Priority, Responsible Dept, Initial Estimated date,
          Current Estimated date, Issue resolved date, Delayed days, Remarks. Every row must match an
          existing project and department by name before anything is imported - nothing is saved until
          every row is clean.
        </p>

        <div className="glass-card p-6 mb-6">
          <button
            onClick={handleDownloadSample}
            disabled={downloadingSample}
            className="text-sm font-semibold text-komal-blue hover:underline mb-1 disabled:opacity-60"
          >
            {downloadingSample ? 'Generating...' : '⬇ Download Sample .xlsx (with built-in dropdown menus)'}
          </button>
          <p className="text-xs text-gray-400 mb-4">
            The Issue Type, Project Name, Responsible Dept, and Issue Priority columns come with real
            in-cell dropdown lists pulled from your live data - pick from the list instead of typing, so
            spelling can't be wrong. Fill it out and upload the same file below.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <input type="file" accept=".xlsx,.xlsm,.csv" onChange={handleFileChange} className="text-sm" />
            <button
              onClick={handleValidate}
              disabled={!file || validating}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
            >
              {validating ? 'Checking...' : 'Check File'}
            </button>
            {report?.valid && (
              <button
                onClick={handleCommit}
                disabled={committing}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {committing ? 'Importing...' : `Import ${report.valid_row_count} Entries`}
              </button>
            )}
          </div>
        </div>

        {report && !report.valid && (
          <div className="glass-card">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-red-700">
                {report.error_count} row{report.error_count === 1 ? '' : 's'} need fixing
                {report.total_rows ? ` (out of ${report.total_rows} checked)` : ''}
              </h2>
              {report.expected_columns && (
                <p className="text-xs text-gray-500 mt-1">
                  Expected columns: {report.expected_columns.join(', ')}
                </p>
              )}
            </div>
            <ul className="divide-y divide-gray-100">
              {report.errors.map((err) => (
                <li key={err.row} className="px-5 py-3 text-sm">
                  <div className="font-semibold text-gray-800">
                    {err.row > 0 ? `Row ${err.row}` : 'File'}
                    {err.issues_preview ? ` — "${err.issues_preview}"` : ''}
                  </div>
                  <ul className="mt-1 space-y-1">
                    {err.problems.map((p, i) => (
                      <li key={i} className="text-xs text-gray-600">
                        <span className="font-medium">{p.field}</span>
                        {p.value ? ` = "${p.value}"` : ''}: {p.message}
                        {p.suggestions && p.suggestions.length > 0 && (
                          <span className="text-komal-blue"> — did you mean: {p.suggestions.join(', ')}?</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        )}

        {report?.valid && (
          <div className="glass-card">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-green-700">
                {report.valid_row_count} row{report.valid_row_count === 1 ? '' : 's'} ready to import
              </h2>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-2 font-medium">Type</th>
                  <th className="px-5 py-2 font-medium">Project</th>
                  <th className="px-5 py-2 font-medium">Description</th>
                  <th className="px-5 py-2 font-medium">Severity</th>
                  <th className="px-5 py-2 font-medium">Dept</th>
                  <th className="px-5 py-2 font-medium">Due</th>
                  <th className="px-5 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {report.parsed_rows.slice(0, 50).map((r, i) => (
                  <tr key={i} className="border-b border-gray-50 last:border-0">
                    <td className="px-5 py-2 capitalize">{r.entry_type}</td>
                    <td className="px-5 py-2">{r.project_name}</td>
                    <td className="px-5 py-2 max-w-xs truncate">{r.issue_description}</td>
                    <td className="px-5 py-2 capitalize">{r.severity}</td>
                    <td className="px-5 py-2">{r.assigned_to_dept_name || '—'}</td>
                    <td className="px-5 py-2">{r.expected_completion_date}</td>
                    <td className="px-5 py-2 capitalize">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {report.parsed_rows.length > 50 && (
              <p className="text-xs text-gray-400 px-5 py-3">
                ...and {report.parsed_rows.length - 50} more row(s), not shown in this preview.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskIssueImportPage;
